/**
 * v1.14.61.0 F4 — Quoter Public Page (Cliente).
 *
 * Landing pubblica brandizzata Colucci accessibile via link condiviso dal
 * broker: `/q/{token}`. Step 1 = scarica/compila/carica l'Excel anagrafica.
 * Step 2 (F5) = wizard garanzie. Step 3 = riepilogo + download artefatti.
 *
 * Bypassa il SiteAccessGate (vedi App.js — isQuoterPublic).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Download,
  Upload,
  CheckCircle2,
  AlertCircle,
  Loader2,
  FileSpreadsheet,
  Building2,
  ArrowRight,
  ShieldCheck,
  XCircle,
  Clock,
} from "lucide-react";
import GaranzieWizard from "./GaranzieWizard";
import GaranzieWizardBulk from "./GaranzieWizardBulk";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PALETTE = {
  navy: "#0B2545",
  navyDark: "#06182E",
  gold: "#C9A961",
  goldSoft: "#E2C988",
  cream: "#F8F4ED",
  ink: "#1A1A1A",
  text: "#3A3A3A",
  muted: "#6E6E6E",
  border: "#E5DFD4",
};

const STEPS = [
  { id: "anagrafica", label: "Anagrafica", icon: FileSpreadsheet },
  { id: "garanzie", label: "Garanzie", icon: ShieldCheck },
  { id: "riepilogo", label: "Riepilogo", icon: CheckCircle2 },
];

// ─────────────────────────────────────────────────────────────────────────
// Status fetcher con polling leggero
// ─────────────────────────────────────────────────────────────────────────

function useInviteStatus(token) {
  const [data, setData] = useState({ loading: true });

  const fetchStatus = useCallback(async () => {
    try {
      const { data: d } = await axios.get(
        `${API}/public/quoter/invites/${token}/status`,
      );
      setData({ loading: false, ...d });
    } catch (err) {
      setData({
        loading: false,
        error: err.response?.data?.detail || "network_error",
      });
    }
  }, [token]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  return { ...data, refresh: fetchStatus };
}

// ─────────────────────────────────────────────────────────────────────────
// Layout / Header brandizzato
// ─────────────────────────────────────────────────────────────────────────

const BrandHeader = ({ subtitle }) => (
  <header
    data-testid="quoter-public-header"
    className="w-full border-b"
    style={{ backgroundColor: PALETTE.navy, borderColor: PALETTE.navyDark }}
  >
    <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div
          className="w-9 h-9 rounded-md flex items-center justify-center text-lg font-bold"
          style={{ background: PALETTE.gold, color: PALETTE.navy }}
        >
          C
        </div>
        <div>
          <div className="text-white font-semibold tracking-wide text-sm uppercase">
            Colucci Assicuratori
          </div>
          {subtitle ? (
            <div className="text-[11px]" style={{ color: PALETTE.goldSoft }}>
              {subtitle}
            </div>
          ) : null}
        </div>
      </div>
      <div className="hidden sm:flex items-center gap-3 text-xs text-white/70">
        <span>📞 081 761 1000</span>
        <span>·</span>
        <span>info@colucciassicuratori.com</span>
      </div>
    </div>
  </header>
);

const Stepper = ({ currentStep }) => {
  const idx = STEPS.findIndex((s) => s.id === currentStep);
  return (
    <div
      data-testid="quoter-stepper"
      className="flex items-center gap-2 max-w-2xl mx-auto px-6 -mt-5"
    >
      {STEPS.map((step, i) => {
        const active = i === idx;
        const done = i < idx;
        const Icon = step.icon;
        return (
          <React.Fragment key={step.id}>
            <div
              data-testid={`step-${step.id}-${active ? "active" : done ? "done" : "pending"}`}
              className="flex items-center gap-2 px-3 py-2 rounded-full bg-white shadow-sm border"
              style={{
                borderColor: active ? PALETTE.gold : PALETTE.border,
              }}
            >
              <Icon
                className="w-4 h-4"
                style={{
                  color: done
                    ? "#1E8E5C"
                    : active
                    ? PALETTE.navy
                    : PALETTE.muted,
                }}
              />
              <span
                className="text-xs font-medium"
                style={{
                  color: active ? PALETTE.navy : PALETTE.muted,
                }}
              >
                {step.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className="flex-1 h-px" style={{ background: PALETTE.border }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Card primary (cream box)
// ─────────────────────────────────────────────────────────────────────────

const CreamCard = ({ children, title, subtitle, accent = false, testid }) => (
  <div
    data-testid={testid}
    className="rounded-2xl shadow-sm border overflow-hidden"
    style={{ borderColor: PALETTE.border, backgroundColor: "#FFFFFF" }}
  >
    {(title || subtitle) && (
      <div
        className="px-6 py-5 border-b"
        style={{
          background: accent ? PALETTE.cream : "transparent",
          borderColor: PALETTE.border,
        }}
      >
        {title && (
          <h2
            className="font-serif text-xl tracking-tight"
            style={{ color: PALETTE.navy, fontFamily: "Georgia, serif" }}
          >
            {title}
          </h2>
        )}
        {subtitle && (
          <p className="text-sm mt-1" style={{ color: PALETTE.muted }}>
            {subtitle}
          </p>
        )}
      </div>
    )}
    <div className="px-6 py-5">{children}</div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────
// FullScreen messages (loading/error)
// ─────────────────────────────────────────────────────────────────────────

const FullScreenState = ({ icon: Icon, title, message, color = PALETTE.navy }) => (
  <div
    className="min-h-screen flex flex-col items-center justify-center px-6"
    style={{ backgroundColor: PALETTE.cream }}
  >
    <Icon className="w-12 h-12 mb-4" style={{ color }} />
    <h1
      className="text-2xl font-semibold text-center"
      style={{ color: PALETTE.navy, fontFamily: "Georgia, serif" }}
    >
      {title}
    </h1>
    {message && (
      <p
        className="text-base max-w-md text-center mt-3"
        style={{ color: PALETTE.text }}
      >
        {message}
      </p>
    )}
    <p className="text-xs mt-8" style={{ color: PALETTE.muted }}>
      Colucci Assicuratori · 📞 081 761 1000
    </p>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────
// Step 1 — Anagrafica (download template + upload Excel + preview)
// ─────────────────────────────────────────────────────────────────────────

function AnagraficaStep({ token, status, onUploaded }) {
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const fileRef = useRef(null);

  // v1.14.61.0 fix UI #1 — Se lo status backend indica anagrafica già
  // caricata, fetcha la preview e mostra la view "uploaded".
  const shouldShowPreview =
    status?.status === "anagrafica_uploaded" ||
    status?.status === "garanzie_in_progress";

  useEffect(() => {
    if (!shouldShowPreview || preview) return;
    setPreviewLoading(true);
    axios
      .get(`${API}/public/quoter/invites/${token}/anagrafica-preview`)
      .then(({ data }) => {
        setPreview(data.preview || []);
        setErrors(data.errors_per_row || []);
      })
      .catch(() => {})
      .finally(() => setPreviewLoading(false));
  }, [shouldShowPreview, token, preview]);

  const downloadTemplate = async () => {
    setDownloading(true);
    try {
      const resp = await axios.get(
        `${API}/public/quoter/invites/${token}/anagrafica-template`,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(resp.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `anagrafica_colucci_${token.slice(0, 8)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Template Excel scaricato");
    } catch (e) {
      toast.error("Errore download template");
    } finally {
      setDownloading(false);
    }
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("Carica un file .xlsx");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("File troppo grande (max 8 MB)");
      return;
    }
    setUploading(true);
    setErrors([]);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(
        `${API}/public/quoter/invites/${token}/anagrafica-upload`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setPreview(data.preview);
      setErrors(data.errors_per_row || []);
      toast.success(
        `${data.total_rows} immobil${data.total_rows === 1 ? "e" : "i"} caricat${data.total_rows === 1 ? "o" : "i"}`,
      );
      // NB: non auto-avanzo allo Step 2: il cliente deve poter vedere il
      // preview dell'anagrafica e cliccare 'Prosegui allo Step 2'.
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg =
        typeof detail === "object"
          ? detail.message
          : detail || "Errore caricamento";
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]);
  };

  if (previewLoading) {
    return (
      <CreamCard testid="anagrafica-loading" title="Caricamento anagrafica…">
        <div className="flex items-center gap-2 p-4">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm" style={{ color: PALETTE.muted }}>
            Recupero degli immobili in corso…
          </span>
        </div>
      </CreamCard>
    );
  }

  if (preview && preview.length > 0) {
    return (
      <CreamCard
        testid="anagrafica-uploaded"
        title={`${preview.length} immobil${preview.length === 1 ? "e" : "i"} ricevut${preview.length === 1 ? "o" : "i"}`}
        subtitle="Verifica i dati e procedi alla scelta delle garanzie."
        accent
      >
        {errors?.length > 0 && (
          <div
            data-testid="anagrafica-warnings"
            className="mb-4 p-3 rounded-lg border text-sm"
            style={{
              borderColor: "#E2B97A",
              background: "#FFF7E8",
              color: "#7A4F00",
            }}
          >
            <strong>Attenzione</strong> — alcune righe presentano avvisi:
            <ul className="list-disc ml-5 mt-1">
              {errors.slice(0, 5).map((e, i) => (
                <li key={i}>
                  Riga {e.row}: {(e.errors || []).join("; ")}
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="overflow-x-auto -mx-2" data-testid="immobili-preview-list">
          <table className="w-full text-sm">
            <thead>
              <tr
                className="text-left"
                style={{ background: PALETTE.cream, color: PALETTE.navy }}
              >
                <th className="px-3 py-2 font-medium">ID</th>
                <th className="px-3 py-2 font-medium">Indirizzo</th>
                <th className="px-3 py-2 font-medium">Comune</th>
                <th className="px-3 py-2 font-medium">Attività</th>
                <th className="px-3 py-2 font-medium">Settore</th>
                <th className="px-3 py-2 font-medium">Classe</th>
                <th className="px-3 py-2 font-medium text-right">
                  Valore Fabb.
                </th>
              </tr>
            </thead>
            <tbody>
              {preview.map((u, i) => (
                <tr
                  key={i}
                  className="border-t"
                  style={{ borderColor: PALETTE.border }}
                  data-testid={`anagrafica-row-${i}`}
                >
                  <td className="px-3 py-2 font-mono text-xs">
                    {u.ubicazione_id}
                  </td>
                  <td className="px-3 py-2">
                    {u.indirizzo}
                    <div className="text-xs" style={{ color: PALETTE.muted }}>
                      {u.cap} {u.comune} ({u.provincia})
                    </div>
                  </td>
                  <td className="px-3 py-2">{u.comune}</td>
                  <td className="px-3 py-2">
                    {u.attivita_descrizione || "—"}
                    {u._attivita_needs_review && (
                      <span
                        className="ml-2 text-[10px] px-1.5 py-0.5 rounded"
                        style={{
                          background: "#FFE7B5",
                          color: "#7A4F00",
                        }}
                      >
                        Da verificare
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {u.settore || "—"}
                    {u.settore_overridden_from && (
                      <div className="text-[10px]" style={{ color: PALETTE.muted }}>
                        (era: {u.settore_overridden_from})
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className="text-[10px] px-2 py-0.5 rounded-full"
                      style={{
                        background:
                          u.classe_territoriale === "I"
                            ? "#FCE7E7"
                            : u.classe_territoriale === "II"
                            ? "#FFF6D6"
                            : "#E8F5E9",
                        color:
                          u.classe_territoriale === "I"
                            ? "#8B1A1A"
                            : u.classe_territoriale === "II"
                            ? "#7A5800"
                            : "#1B5E20",
                      }}
                    >
                      cl. {u.classe_territoriale}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {(u.valore_fabbricato_eur || 0).toLocaleString("it-IT")} €
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <button
            data-testid="anagrafica-reupload-btn"
            className="text-sm underline"
            style={{ color: PALETTE.muted }}
            onClick={() => {
              setPreview(null);
              setErrors([]);
            }}
          >
            Ricarica un altro Excel
          </button>
          <button
            data-testid="goto-garanzie-btn"
            onClick={() => onUploaded?.()}
            className="px-5 py-3 rounded-full font-medium inline-flex items-center gap-2 transition hover:opacity-90"
            style={{ background: PALETTE.navy, color: "white" }}
          >
            Prosegui allo Step 2 — Garanzie
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        <p className="mt-3 text-xs" style={{ color: PALETTE.muted }}>
          Nello Step 2 sceglierai le coperture e i massimali per ogni immobile
          della tua anagrafica.
        </p>
      </CreamCard>
    );
  }

  return (
    <CreamCard
      testid="anagrafica-empty"
      title="Step 1 — Anagrafica immobili"
      subtitle="Scarica il template Excel, compila una riga per ogni immobile e ricarica il file."
      accent
    >
      <div className="grid sm:grid-cols-2 gap-5">
        {/* Download */}
        <div
          className="rounded-xl p-5 border h-full flex flex-col"
          style={{ borderColor: PALETTE.border, background: PALETTE.cream }}
        >
          <div className="flex items-center gap-2 mb-2">
            <FileSpreadsheet
              className="w-5 h-5"
              style={{ color: PALETTE.navy }}
            />
            <div
              className="font-semibold"
              style={{ color: PALETTE.navy }}
            >
              1. Scarica il template
            </div>
          </div>
          <p
            className="text-sm flex-1"
            style={{ color: PALETTE.text }}
          >
            Excel con 17 colonne anagrafiche (indirizzo, attività, valori).
            Tipo Attività e Provincia hanno menu a tendina chiusi.
          </p>
          <button
            data-testid="download-template-btn"
            onClick={downloadTemplate}
            disabled={downloading}
            className="mt-4 px-4 py-2.5 rounded-full inline-flex items-center gap-2 font-medium text-sm transition"
            style={{
              background: PALETTE.gold,
              color: PALETTE.navy,
            }}
          >
            {downloading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            Scarica Excel anagrafica
          </button>
        </div>

        {/* Upload */}
        <div
          data-testid="upload-drop-zone"
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          onClick={() => fileRef.current?.click()}
          className="rounded-xl p-5 border-2 border-dashed h-full flex flex-col items-center justify-center text-center cursor-pointer transition"
          style={{
            borderColor: uploading ? PALETTE.gold : PALETTE.border,
            background: uploading ? "#FFF6E0" : "#FFFFFF",
          }}
        >
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid="upload-file-input"
          />
          {uploading ? (
            <>
              <Loader2
                className="w-7 h-7 mb-3 animate-spin"
                style={{ color: PALETTE.navy }}
              />
              <div className="font-semibold" style={{ color: PALETTE.navy }}>
                Analisi in corso…
              </div>
            </>
          ) : (
            <>
              <Upload
                className="w-7 h-7 mb-3"
                style={{ color: PALETTE.navy }}
              />
              <div className="font-semibold" style={{ color: PALETTE.navy }}>
                2. Carica l&apos;Excel compilato
              </div>
              <div
                className="text-xs mt-1"
                style={{ color: PALETTE.muted }}
              >
                Trascina qui o clicca per scegliere il file .xlsx
              </div>
            </>
          )}
        </div>
      </div>

      <div
        className="mt-5 p-3 rounded-lg flex items-start gap-2 text-xs"
        style={{
          background: PALETTE.cream,
          color: PALETTE.text,
        }}
      >
        <Building2
          className="w-4 h-4 mt-0.5 shrink-0"
          style={{ color: PALETTE.navy }}
        />
        <div>
          <strong>Riservatezza</strong> — i dati sono riservati al tuo agente
          Colucci. Il link è personale: non condividerlo. La quotazione resta
          una proposta e non vincola le parti.
        </div>
      </div>
    </CreamCard>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// PAGE MAIN
// ─────────────────────────────────────────────────────────────────────────

export default function QuoterPublicPage() {
  const { token } = useParams();
  const status = useInviteStatus(token);
  const [overrideStep, setOverrideStep] = useState(null);
  const [finalQuotation, setFinalQuotation] = useState(null);
  // v1.14.61.2.0 — flag locale per forzare vista pending dopo submit
  // (lo status hook potrebbe non aver ancora rilevato il cambio backend)
  const [forcePending, setForcePending] = useState(false);

  const derivedStep = useMemo(() => {
    if (finalQuotation || forcePending) return "pending_broker_review";
    if (overrideStep) return overrideStep;
    if (status?.step === "pending_broker_review")
      return "pending_broker_review";
    if (status?.step === "completed") return "riepilogo";
    if (status?.step === "garanzie") return "garanzie";
    return "anagrafica";
  }, [overrideStep, status?.step, finalQuotation, forcePending]);

  const titleByStatus = useMemo(() => {
    if (!status || status.loading) return "Caricamento…";
    if (status.error || !status.valid) return "Link non disponibile";
    return "Preventivo Personalizzato";
  }, [status]);

  if (status?.loading) {
    return (
      <FullScreenState
        icon={Loader2}
        title="Apertura preventivo…"
        message="Stiamo verificando il link che hai ricevuto."
      />
    );
  }

  if (status?.error || (!status?.valid && !status?.completed)) {
    if (status?.expired) {
      return (
        <FullScreenState
          icon={Clock}
          title="Link scaduto"
          message="Questo link è scaduto. Contatta il tuo agente Colucci per riceverne uno nuovo."
          color="#7A4F00"
        />
      );
    }
    if (status?.revoked) {
      return (
        <FullScreenState
          icon={XCircle}
          title="Link revocato"
          message="L'agenzia ha revocato questo link. Per assistenza chiama Colucci Assicuratori."
          color="#8B1A1A"
        />
      );
    }
    return (
      <FullScreenState
        icon={AlertCircle}
        title="Link non valido"
        message="Il link che hai aperto non è riconosciuto. Verifica di aver copiato l'URL completo dal messaggio del tuo agente Colucci."
        color="#8B1A1A"
      />
    );
  }

  // v1.14.61.2.0 — Broker-gated views
  if (status?.pending_broker_review) {
    return <PendingBrokerReviewView token={token} />;
  }
  if (status?.transmitted || status?.completed) {
    return <CompletedDownloadView token={token} />;
  }

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: PALETTE.cream }}
    >
      <BrandHeader subtitle="Quotazione Composita Reale — Reale Mutua" />
      {/* Hero */}
      <section
        className="border-b"
        style={{
          background: PALETTE.navy,
          borderColor: PALETTE.navyDark,
        }}
      >
        <div className="max-w-5xl mx-auto px-6 pt-8 pb-12">
          <p
            className="text-[11px] font-semibold tracking-[0.2em] uppercase"
            style={{ color: PALETTE.goldSoft }}
          >
            {titleByStatus}
          </p>
          <h1
            className="mt-2 text-3xl sm:text-4xl font-semibold"
            style={{
              color: "white",
              fontFamily: "Georgia, serif",
              lineHeight: 1.15,
            }}
          >
            Riceverai la tua quotazione in 3 semplici step.
          </h1>
          <p className="mt-3 text-base max-w-2xl" style={{ color: "#D5DDEA" }}>
            Compila l&apos;anagrafica degli immobili, scegli le garanzie e ricevi il
            preventivo brandizzato Reale Mutua, completo di Excel riepilogo e
            Fascicolo Informativo (CGA).
          </p>
        </div>
      </section>

      <Stepper currentStep={derivedStep} />

      <main className="flex-1 max-w-5xl mx-auto px-6 py-10 w-full">
        {derivedStep === "anagrafica" && (
          <AnagraficaStep
            token={token}
            status={status}
            onUploaded={() => setOverrideStep("garanzie")}
          />
        )}
        {derivedStep === "garanzie" && (
          (() => {
            const params = new URLSearchParams(window.location.search);
            // v1.14.61.1.0 — Wizard v2 Bulk è il default. Fallback legacy
            // via `?wizard=v1` solo per retrocompatibilità.
            const useLegacy = params.get("wizard") === "v1";
            const WizardComp = useLegacy ? GaranzieWizard : GaranzieWizardBulk;
            return (
              <WizardComp
                token={token}
                onSubmitted={(data) => {
                  setFinalQuotation(data);
                  setForcePending(true);
                }}
                onBack={() => setOverrideStep("anagrafica")}
              />
            );
          })()
        )}
        {derivedStep === "riepilogo" && (
          <RiepilogoFinale token={token} quotation={finalQuotation} />
        )}
        {derivedStep === "pending_broker_review" && (
          <PendingBrokerReviewCard />
        )}
      </main>

      <footer
        className="border-t py-5 px-6 text-xs text-center"
        style={{ borderColor: PALETTE.border, color: PALETTE.muted }}
      >
        © Colucci Assicuratori S.r.l. · IVASS A000464752 · Sede legale Portici
        (NA) · Link sicuro · Cookie tecnici non profilanti
      </footer>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// Riepilogo finale post-submit (F5 result + F6 download placeholders)
// ─────────────────────────────────────────────────────────────────────────

function RiepilogoFinale({ token, quotation }) {
  const fmt = (n) =>
    new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 2,
    }).format(Number(n || 0));

  if (!quotation) {
    return (
      <CreamCard
        testid="riepilogo-already-done"
        title="Preventivo completato"
        subtitle="La tua quotazione è stata già emessa."
        accent
      >
        <p className="text-sm" style={{ color: PALETTE.text }}>
          Il tuo agente Colucci ha ricevuto la conferma. Per gli artefatti
          (PDF preventivo + Excel riepilogo + Fascicolo Informativo)
          contatta l'agenzia o attendi la sua mail.
        </p>
      </CreamCard>
    );
  }

  const dl = (kind) => async () => {
    try {
      const resp = await axios.get(
        `${API}/public/quoter/invites/${token}/artifacts/${kind}`,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      const ext = kind === "excel" ? "xlsx" : "pdf";
      a.download = `${quotation.preventivo_numero}_${kind}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Artefatto non ancora disponibile",
      );
    }
  };

  return (
    <CreamCard
      testid="riepilogo-finale"
      title="Preventivo emesso"
      subtitle={`Numero: ${quotation.preventivo_numero}`}
      accent
    >
      <div className="grid sm:grid-cols-2 gap-4">
        <div
          className="rounded-lg p-4 border"
          style={{ borderColor: PALETTE.border, background: "#FFFFFF" }}
        >
          <div className="text-xs uppercase" style={{ color: PALETTE.muted }}>
            Premio annuo lordo
          </div>
          <div
            className="text-3xl font-semibold mt-1"
            style={{ color: PALETTE.navy }}
            data-testid="riepilogo-totale-eur"
          >
            {fmt(quotation.totale_premio_eur)}
          </div>
          <div className="text-xs mt-2" style={{ color: PALETTE.muted }}>
            {quotation.ubicazioni_count} immobili · {fmt(quotation.totale_premio_netto_eur)} netto
            · {fmt(quotation.totale_accessori_eur)} accessori · {fmt(quotation.totale_imposte_eur)} imposte
            · {fmt(quotation.diritti_emissione_eur)} diritti
          </div>
        </div>

        <div
          className="rounded-lg p-4 border"
          style={{ borderColor: PALETTE.border, background: "#FFFFFF" }}
        >
          <div className="text-xs uppercase mb-2" style={{ color: PALETTE.muted }}>
            Scarica i documenti
          </div>
          <div className="flex flex-col gap-2">
            <button
              onClick={dl("pdf")}
              data-testid="dl-pdf-btn"
              className="px-4 py-2 rounded-full text-sm font-medium inline-flex items-center justify-center gap-2"
              style={{ background: PALETTE.gold, color: PALETTE.navy }}
            >
              <Download className="w-4 h-4" />
              PDF Preventivo + CGA
            </button>
            <button
              onClick={dl("excel")}
              data-testid="dl-excel-btn"
              className="px-4 py-2 rounded-full text-sm font-medium inline-flex items-center justify-center gap-2 border"
              style={{ borderColor: PALETTE.border, color: PALETTE.navy }}
            >
              <Download className="w-4 h-4" />
              Excel Riepilogo
            </button>
            <button
              onClick={dl("cga")}
              data-testid="dl-cga-btn"
              className="px-4 py-2 rounded-full text-sm font-medium inline-flex items-center justify-center gap-2 border"
              style={{ borderColor: PALETTE.border, color: PALETTE.navy }}
            >
              <Download className="w-4 h-4" />
              Fascicolo Informativo (CGA)
            </button>
          </div>
        </div>
      </div>

      {quotation.multi_ub_warning && (
        <div
          className="mt-4 p-3 rounded-lg text-sm border"
          style={{
            borderColor: "#E2B97A",
            background: "#FFF7E8",
            color: "#7A4F00",
          }}
          data-testid="multi-ub-warning"
        >
          {quotation.multi_ub_warning}
        </div>
      )}

      <p className="mt-5 text-xs" style={{ color: PALETTE.muted }}>
        Il tuo agente Colucci riceve automaticamente lo stesso preventivo per
        l&apos;emissione. La quotazione resta una proposta e non vincola le
        parti.
      </p>
    </CreamCard>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// v1.14.61.0.1 — Vista completata con i 3 bottoni download artefatti.
// Sostituisce il vecchio FullScreenState statico: il cliente ottiene i suoi
// 3 file (Excel/PDF/CGA) direttamente dal link, anche dopo il submit.
// ─────────────────────────────────────────────────────────────────────────

function CompletedDownloadView({ token }) {
  const [busy, setBusy] = useState(null);

  const dl = async (kind, ext, label) => {
    setBusy(kind);
    try {
      const resp = await axios.get(
        `${API}/public/quoter/invites/${token}/artifacts/${kind}`,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `colucci_${kind}_${token.slice(0, 8)}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${label} scaricato`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : detail?.message || `Errore download ${kind}`,
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: PALETTE.cream }}
    >
      <BrandHeader subtitle="Quotazione Composita Reale — Reale Mutua" />
      <section
        className="border-b"
        style={{
          background: PALETTE.navy,
          borderColor: PALETTE.navyDark,
        }}
      >
        <div className="max-w-3xl mx-auto px-6 py-10 text-center">
          <CheckCircle2
            className="w-14 h-14 mx-auto mb-3"
            style={{ color: PALETTE.goldSoft }}
          />
          <p
            className="text-[11px] font-semibold tracking-[0.2em] uppercase"
            style={{ color: PALETTE.goldSoft }}
          >
            Preventivo Completato
          </p>
          <h1
            className="mt-2 text-3xl sm:text-4xl font-semibold"
            style={{
              color: "white",
              fontFamily: "Georgia, serif",
              lineHeight: 1.2,
            }}
          >
            La tua quotazione è pronta.
          </h1>
          <p
            className="mt-3 text-base"
            style={{ color: "#D5DDEA" }}
          >
            Scarica i 3 documenti qui sotto. Il tuo agente Colucci li ha
            ricevuti in copia per l&apos;emissione della polizza.
          </p>
        </div>
      </section>

      <main className="flex-1 max-w-3xl mx-auto px-6 py-10 w-full">
        <div
          data-testid="completed-downloads-card"
          className="rounded-2xl shadow-sm border bg-white"
          style={{ borderColor: PALETTE.border }}
        >
          <div
            className="px-6 py-5 border-b"
            style={{
              borderColor: PALETTE.border,
              background: PALETTE.cream,
            }}
          >
            <h2
              className="font-serif text-xl"
              style={{
                color: PALETTE.navy,
                fontFamily: "Georgia, serif",
              }}
            >
              Scarica i tuoi documenti
            </h2>
            <p className="text-xs mt-1" style={{ color: PALETTE.muted }}>
              I file sono firmati con la nostra intestazione e contengono
              tutti i dati della tua quotazione.
            </p>
          </div>
          <div className="p-6 grid sm:grid-cols-3 gap-4">
            <button
              data-testid="download-pdf"
              disabled={busy === "pdf"}
              onClick={() => dl("pdf", "pdf", "PDF preventivo")}
              className="rounded-xl border p-5 text-center transition hover:shadow-md disabled:opacity-60"
              style={{ borderColor: PALETTE.border, background: PALETTE.cream }}
            >
              {busy === "pdf" ? (
                <Loader2 className="w-7 h-7 mx-auto mb-2 animate-spin" style={{ color: PALETTE.navy }} />
              ) : (
                <Download className="w-7 h-7 mx-auto mb-2" style={{ color: PALETTE.navy }} />
              )}
              <div className="font-semibold text-sm" style={{ color: PALETTE.navy }}>
                PDF Preventivo + CGA
              </div>
              <div className="text-[11px] mt-1" style={{ color: PALETTE.muted }}>
                Documento principale brandizzato
              </div>
            </button>

            <button
              data-testid="download-excel"
              disabled={busy === "excel"}
              onClick={() => dl("excel", "xlsx", "Excel riepilogo")}
              className="rounded-xl border p-5 text-center transition hover:shadow-md disabled:opacity-60"
              style={{ borderColor: PALETTE.border, background: PALETTE.cream }}
            >
              {busy === "excel" ? (
                <Loader2 className="w-7 h-7 mx-auto mb-2 animate-spin" style={{ color: PALETTE.navy }} />
              ) : (
                <FileSpreadsheet className="w-7 h-7 mx-auto mb-2" style={{ color: PALETTE.navy }} />
              )}
              <div className="font-semibold text-sm" style={{ color: PALETTE.navy }}>
                Excel Riepilogo
              </div>
              <div className="text-[11px] mt-1" style={{ color: PALETTE.muted }}>
                3 fogli: anagrafica · sezioni · oneri
              </div>
            </button>

            <button
              data-testid="download-cga"
              disabled={busy === "cga"}
              onClick={() => dl("cga", "pdf", "Fascicolo informativo")}
              className="rounded-xl border p-5 text-center transition hover:shadow-md disabled:opacity-60"
              style={{ borderColor: PALETTE.border, background: PALETTE.cream }}
            >
              {busy === "cga" ? (
                <Loader2 className="w-7 h-7 mx-auto mb-2 animate-spin" style={{ color: PALETTE.navy }} />
              ) : (
                <Download className="w-7 h-7 mx-auto mb-2" style={{ color: PALETTE.navy }} />
              )}
              <div className="font-semibold text-sm" style={{ color: PALETTE.navy }}>
                Fascicolo Informativo (CGA)
              </div>
              <div className="text-[11px] mt-1" style={{ color: PALETTE.muted }}>
                Condizioni di Assicurazione complete
              </div>
            </button>
          </div>
        </div>

        <p className="mt-6 text-xs text-center" style={{ color: PALETTE.muted }}>
          Per qualsiasi domanda contatta Colucci Assicuratori — 📞 081 761 1000 — info@colucciassicuratori.com
        </p>
      </main>

      <footer
        className="border-t py-5 px-6 text-xs text-center"
        style={{ borderColor: PALETTE.border, color: PALETTE.muted }}
      >
        © Colucci Assicuratori S.r.l. · IVASS A000464752 · Sede legale Portici (NA) · Link sicuro
      </footer>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// v1.14.61.2.0 — Broker-gated views
// ─────────────────────────────────────────────────────────────────────────

function PendingBrokerReviewCard() {
  return (
    <CreamCard
      title="✓ Le tue richieste sono state ricevute"
      subtitle="L'agenzia sta finalizzando la quotazione"
    >
      <div
        data-testid="pending-broker-card"
        className="rounded-lg p-6"
        style={{
          background: "linear-gradient(135deg, #FFFDF8 0%, #F8F4ED 100%)",
          border: `1px solid ${PALETTE.gold}`,
        }}
      >
        <div className="flex items-start gap-4">
          <div
            className="rounded-full p-3 flex-shrink-0"
            style={{ background: PALETTE.gold }}
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <path d="M5 12l5 5L20 7" stroke={PALETTE.navyDark}
                     strokeWidth="3" strokeLinecap="round"
                     strokeLinejoin="round" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold mb-2"
                 style={{ color: PALETTE.navyDark }}>
              Richiesta ricevuta correttamente
            </h3>
            <p className="text-sm mb-3" style={{ color: PALETTE.text }}>
              Il tuo agente <strong>Colucci Assicuratori</strong> sta
              finalizzando la quotazione personalizzata sui dati che hai
              fornito. Riceverai presto la proposta completa via email o
              tramite il tuo consulente di fiducia.
            </p>
            <div className="flex items-center gap-2 text-xs mt-4"
                  style={{ color: PALETTE.muted }}>
              <div className="flex items-center gap-1.5"
                    style={{ color: PALETTE.navy, fontWeight: 600 }}>
                <span className="inline-block w-2 h-2 rounded-full animate-pulse"
                       style={{ background: PALETTE.gold }} />
                Stato richiesta: In lavorazione
              </div>
            </div>
          </div>
        </div>
      </div>
    </CreamCard>
  );
}

function PendingBrokerReviewView({ token }) {
  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: PALETTE.cream }}
      data-testid="pending-broker-view"
    >
      <BrandHeader subtitle="Quotazione Composita Reale — Reale Mutua" />
      <main className="flex-1 max-w-3xl mx-auto px-6 py-10 w-full">
        <PendingBrokerReviewCard />
      </main>
      <footer
        className="border-t py-5 px-6 text-xs text-center"
        style={{ borderColor: PALETTE.border, color: PALETTE.muted }}
      >
        Token: <code>{token}</code> · Colucci Assicuratori © 2026
      </footer>
    </div>
  );
}
